import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, ArrowRight, Download, Smartphone, Mail, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const status = searchParams.get('status');
  const userId = searchParams.get('user_id');
  const paymentId = searchParams.get('payment_id') || searchParams.get('collection_id');

  // Capture PWA install prompt
  useEffect(() => {
    setIsIOS(/iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()));
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    const verifyPayment = async () => {
      if (!userId) {
        setResult({ status: 'error', message: 'No se encontro informacion del pago.' });
        setVerifying(false);
        return;
      }

      try {
        const url = paymentId
          ? `${API_BASE_URL}/api/payments/verify-and-approve/${userId}?payment_id=${paymentId}`
          : `${API_BASE_URL}/api/payments/verify-and-approve/${userId}`;
        const response = await axios.post(url);
        setResult(response.data);
      } catch (error) {
        try {
          const statusResponse = await axios.get(`${API_BASE_URL}/api/payments/status/${userId}`);
          setResult(statusResponse.data);
        } catch (err) {
          setResult({
            status: status || 'error',
            message: status === 'success'
              ? 'Pago recibido. Tu cuenta se activara en breve.'
              : status === 'pending'
              ? 'Tu pago esta siendo procesado.'
              : 'Hubo un problema con el pago.',
          });
        }
      }
      setVerifying(false);
    };

    verifyPayment();
  }, [userId, status, paymentId]);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
    setShowInstallModal(false);
  };

  const getIcon = () => {
    if (verifying) return <Clock size={64} className="text-gray-400 animate-pulse" />;
    const s = result?.status;
    if (s === 'approved' || s === 'already_approved' || s === 'success' || s === 'activation_sent')
      return <CheckCircle size={64} className="text-green-500" />;
    if (s === 'failed' || s === 'error')
      return <XCircle size={64} className="text-red-500" />;
    return <Clock size={64} className="text-yellow-500" />;
  };

  const getTitle = () => {
    if (verifying) return 'Verificando tu pago...';
    const s = result?.status;
    if (s === 'approved' || s === 'already_approved' || s === 'activation_sent') return 'Pago exitoso';
    if (s === 'failed') return 'Pago rechazado';
    if (s === 'pending') return 'Pago en proceso';
    return 'Resultado del pago';
  };

  const isSuccess = result?.status === 'approved' || result?.status === 'already_approved';
  const isActivationSent = result?.status === 'activation_sent';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="payment-result-page">
      <div className="max-w-md w-full text-center">
        <div className="mb-8 flex justify-center">{getIcon()}</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4" data-testid="payment-result-title">
          {getTitle()}
        </h1>

        {verifying && (
          <p className="text-gray-600 mb-10">Estamos confirmando tu pago con MercadoPago...</p>
        )}

        {/* ACTIVATION SENT - Main post-payment screen */}
        {!verifying && isActivationSent && (
          <div className="space-y-5" data-testid="activation-sent-section">
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Mail size={28} className="text-green-600 flex-shrink-0" />
                <p className="text-green-800 font-bold text-lg">Revisa tu correo electronico</p>
              </div>
              <p className="text-green-700 text-base leading-relaxed mb-3">
                Te enviamos un correo con un <strong>boton para activar tu cuenta</strong>. 
                Debes hacer clic en ese boton para poder iniciar sesion.
              </p>
              <p className="text-green-600 text-sm">
                Revisa tu bandeja de entrada y la carpeta de spam.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-left">
              <p className="text-amber-800 text-sm font-medium">
                Importante: No podras entrar a la app hasta que actives tu cuenta desde el correo.
              </p>
            </div>

            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
              data-testid="go-to-login-btn"
            >
              Ir a Iniciar Sesion <ArrowRight size={20} />
            </button>

            <button
              onClick={async () => {
                setResending(true);
                try {
                  const resendUserId = result?.user_id || userId;
                  await axios.post(`${API_BASE_URL}/api/auth/resend-activation/${resendUserId}`);
                  setResendDone(true);
                } catch (err) {
                  console.error('Resend error:', err);
                }
                setResending(false);
              }}
              disabled={resending || resendDone}
              className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-full hover:border-gray-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="resend-activation-btn"
            >
              {resending ? (
                <><RefreshCw size={16} className="animate-spin" /> Reenviando...</>
              ) : resendDone ? (
                'Correo reenviado'
              ) : (
                <><RefreshCw size={16} /> No me llego el correo, reenviar</>
              )}
            </button>
          </div>
        )}

        {/* ALREADY APPROVED */}
        {!verifying && isSuccess && (
          <div className="space-y-4">
            <p className="text-gray-600 mb-6">{result?.message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
              data-testid="go-to-login-btn"
            >
              Iniciar Sesion <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* FAILED */}
        {!verifying && result?.status === 'failed' && (
          <div className="space-y-4">
            <p className="text-gray-600 mb-6">{result?.message}</p>
            <button
              onClick={() => navigate('/register')}
              className="w-full bg-gray-900 text-white font-semibold py-4 rounded-full text-lg hover:bg-gray-800 transition-all flex items-center justify-center gap-2"
            >
              Intentar de nuevo <ArrowRight size={20} />
            </button>
          </div>
        )}

        {/* PENDING */}
        {!verifying && result?.status === 'pending' && (
          <div className="space-y-4">
            <p className="text-gray-600 mb-4">{result?.message}</p>
            <p className="text-sm text-gray-400">
              Tu cuenta se activara automaticamente cuando se confirme el pago.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-full hover:border-gray-500 transition-all"
            >
              Ir al inicio
            </button>
          </div>
        )}

        {/* ERROR / UNKNOWN */}
        {!verifying && (result?.status === 'error' || result?.status === 'none') && (
          <div className="space-y-4">
            <p className="text-gray-600 mb-6">{result?.message}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full border border-gray-300 text-gray-700 font-medium py-3 rounded-full hover:border-gray-500 transition-all"
            >
              Ir al inicio
            </button>
          </div>
        )}
      </div>

      {/* Install PWA Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 px-4" data-testid="install-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl text-center">
            <div className="w-20 h-20 rounded-2xl overflow-hidden mx-auto mb-4 shadow-lg">
              <img src="/icon-192x192.png" alt="Proyecto Residente" className="w-full h-full object-cover" />
            </div>
            <div className="mb-1">
              <Smartphone size={20} className="inline text-gray-400" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Agregar a pantalla de inicio
            </h3>
            {isIOS ? (
              <p className="text-sm text-gray-500 mb-6 leading-relaxed">
                Para instalar la app, toca el boton <strong>Compartir</strong> en Safari y luego selecciona <strong>"Agregar a pantalla de inicio"</strong>.
              </p>
            ) : (
              <p className="text-sm text-gray-500 mb-6">
                Instala Proyecto Residente en tu celular para acceder mas rapido, como una app nativa.
              </p>
            )}
            {!isIOS && deferredPrompt ? (
              <button
                onClick={handleInstall}
                className="w-full bg-gray-900 text-white font-semibold py-3.5 rounded-full text-base hover:bg-gray-800 transition-all flex items-center justify-center gap-2 mb-3"
                data-testid="install-accept-btn"
              >
                <Download size={18} />
                Si, agregar
              </button>
            ) : isIOS ? null : (
              <p className="text-xs text-gray-400 mb-3">
                Tu navegador mostrara la opcion de instalacion automaticamente.
              </p>
            )}
            <button
              onClick={() => setShowInstallModal(false)}
              className="w-full text-gray-400 font-medium py-2 text-sm hover:text-gray-600 transition-colors"
              data-testid="install-dismiss-btn"
            >
              Ahora no
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentResult;
